import { lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Layers, ScrollText, Swords, Trophy, UserPlus } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { useHaptics } from '@/hooks/useHaptics';
import { useCpInvite } from '@/hooks/useCpInvite';
import { useCpQueue } from '@/hooks/useCpQueue';
import { useStartMatch } from '@/hooks/useStartMatch';
import { IconaTutorial } from '@/components/tutorial/IconaTutorial';
import { chiaveTutorialVisto } from '@/lib/storage';
import { CpTutorialModal } from '@/components/chinesepoker/CpTutorialModal';
import { CpInviteSheet } from '@/components/chinesepoker/CpInviteSheet';
import { CpAcceptInviteSheet } from '@/components/chinesepoker/CpAcceptInviteSheet';
import { CpQueueSheet } from '@/components/chinesepoker/CpQueueSheet';
import { useGetLeaderboardQuery } from '@/services/qchinesepokerApi';
import type { Seat } from '@/lib/qcp/protocol';

/**
 * Il livello del tutorial arriva SOLO da qui, con `lazy()`, e SOLO se il Poker Cinese e` nella build: il confronto LETTERALE col flag (`=== 'true'`, la forma di `src/lib/v3.ts`) decide se il `lazy()` esiste.
 * Con il flag spento (produzione) il bundler piega il confronto, il ramo `lazy(() => import(...))` sparisce e con lui il chunk `TutorialCp-*`: `TutorialCp` e` `undefined` e l'icona non si rende.
 * ⚠️ L'annotazione PURE da sola NON basta (misurato il 28/10 sulla 192f2756: il chunk usciva in produzione, perche` `QcpPlayPanel` (Hub) importa questa lobby in modo statico e il `lazy()` a livello di
 * modulo resta); serve il ramo. Terzo lettore del flag dopo `env.ts` e `routes/router.tsx`: la serratura di `tools/verify-qcp.ts` lo dichiara e ne tiene la forma.
 */
const TutorialCp = import.meta.env.VITE_FEATURE_CP_ENABLED === 'true' ? /*#__PURE__*/ lazy(() => import('@/components/chinesepoker/TutorialCp').then((m) => ({ default: m.TutorialCp }))) : undefined;

interface CpLobbyProps {
  /** Hide the top-10 peek when the Hub's Classifica section already shows it. */
  showLeaderboard?: boolean;
}

/**
 * QChinesePoker lobby (Task 10).
 *
 * All 4 entry points are wired:
 *  - "Play vs bot" (T6): mints a client-side match id, navigates to
 *    /chinesepoker/match/:id with state.vsBot = true.
 *  - "Challenge a friend" (T8): opens CpInviteSheet (creator side).
 *    When the invite is accepted the home navigates to the match with seat A.
 *  - "I have a code" (T9): opens CpAcceptInviteSheet (acceptor side).
 *    On success the sheet calls onAccepted → navigate to match with seat B.
 *  - "Ranked queue" (T10): opens CpQueueSheet. The useCpQueue hook exposes
 *    `matched` on mm:matched; an effect here drives startMatch(), which
 *    navigates away and unmounts the sheet (mirrors the invite/practice
 *    flows — no auto-navigation inside the hook itself).
 *
 * T13 extracts the hardcoded English strings into a locale namespace.
 */
export function CpLobby({ showLeaderboard = true }: CpLobbyProps) {
  const { t } = useTranslation('chinesepoker');
  const haptics = useHaptics();
  const startMatch = useStartMatch();

  // Leaderboard (top 10 — quick peek, errors are silent)
  const { data: leaderboard, isLoading: lbLoading, isError: lbError } =
    useGetLeaderboardQuery({ limit: 10 }, { skip: !showLeaderboard });

  // Controllers for the two socket-backed flows
  const invite = useCpInvite();
  const queue = useCpQueue();

  // Which sheet is open
  const [inviteOpen, setInviteOpen] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);

  // Auto-open the rules tutorial on the player's first visit (once).
  useEffect(() => {
    if (!localStorage.getItem('qlash.qcp_tutorial_seen')) {
      setTutorialOpen(true);
      localStorage.setItem('qlash.qcp_tutorial_seen', '1');
    }
  }, []);

  // Creator side: navigate on invite accepted (seat A)
  useEffect(() => {
    if (invite.status !== 'accepted' || !invite.accepted) return;
    haptics.notification('success');
    const { matchId } = invite.accepted;
    const timer = window.setTimeout(() => {
      setInviteOpen(false);
      startMatch({ game: 'qcp', matchId });
    }, 800);
    return () => window.clearTimeout(timer);
  }, [invite.status, invite.accepted, startMatch, haptics]);

  // Ranked queue: on `mm:matched` the hook exposes the paired matchId — start
  // the match exactly like the invite/practice flows do (mirrors the backgammon
  // ranked-paired effect).
  // 🔴 `startMatch` puo' NON far partire niente (c'e' un'altra partita attiva e
  // l'utente rifiuta di abbandonarla), e le dipendenze di questo effetto non
  // cambiano: senza il ramo di ripiego il giocatore resta **abbinato e fermo**,
  // con una partita vera che lo aspetta e nessun gesto per entrarci.
  // ⇒ Sul rifiuto si RIAPRE il foglio, che nel suo ramo `matched` offre «entra»
  // e «lascia la coda». *Il difetto peggiore e' quello che non lascia all'utente
  // nessun gesto* (or0, 11/09).
  useEffect(() => {
    if (!queue.matched) return;
    const partito = startMatch({ game: 'qcp', matchId: queue.matched });
    if (!partito) setQueueOpen(true);
  }, [queue.matched, startMatch]);

  function playVsBot() {
    haptics.impact('medium');
    const id = crypto.randomUUID();
    startMatch({ game: 'qcp', matchId: id, vsBot: true });
  }

  function openInvite() {
    haptics.impact('light');
    setInviteOpen(true);
  }

  function openAccept() {
    haptics.impact('light');
    setAcceptOpen(true);
  }

  function openQueue() {
    haptics.impact('light');
    setQueueOpen(true);
  }

  function closeQueue() {
    queue.leave();
    setQueueOpen(false);
  }

  // Acceptor side: navigate on accept success (seat B)
  function onInviteAccepted(result: { matchId: string; seat: Seat }) {
    haptics.notification('success');
    setAcceptOpen(false);
    startMatch({ game: 'qcp', matchId: result.matchId });
  }

  const players = leaderboard ?? [];

  return (
    <div className="space-y-4 px-4 py-4">
      <header className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand/15 text-brand">
          <Layers aria-hidden className="h-6 w-6" strokeWidth={2.5} />
        </span>
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-fg">{t('home.title')}</h1>
          <p className="text-sm text-fg-muted">{t('home.subtitle')}</p>
        </div>
        {TutorialCp !== undefined && <IconaTutorial Livello={TutorialCp} chiave={chiaveTutorialVisto('chinesepoker')} nome={t('guida.icona.nome')} nuovo={t('guida.icona.nuovo')} />}
        <button
          type="button"
          onClick={() => { haptics.impact('light'); setTutorialOpen(true); }}
          aria-label={t('tutorial.open')}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-border text-fg-muted transition-opacity active:opacity-70"
        >
          <ScrollText aria-hidden className="h-4 w-4" />
        </button>
      </header>

      {/* Primary CTA — vs bot (no server round-trip needed) */}
      <Button fullWidth onClick={playVsBot}>
        {t('home.playVsBot')}
      </Button>

      {/* Ranked queue — opens CpQueueSheet (bucket selection + search) */}
      <button
        type="button"
        onClick={openQueue}
        disabled={queueOpen}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand/15 px-4 py-3 text-sm font-semibold text-brand transition-opacity active:opacity-80 disabled:opacity-60"
      >
        <Swords aria-hidden className="h-4 w-4" />
        {t('home.rankedMatch')}
      </button>

      {/* Friend-challenge + accept-code side-by-side */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={openInvite}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-bg-secondary px-3 py-2.5 text-sm font-semibold text-fg transition-opacity active:opacity-80"
        >
          <UserPlus aria-hidden className="h-4 w-4 text-brand" />
          {t('home.challengeFriend')}
        </button>
        <button
          type="button"
          onClick={openAccept}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-bg-secondary px-3 py-2.5 text-sm font-semibold text-fg transition-opacity active:opacity-80"
        >
          <KeyRound aria-hidden className="h-4 w-4 text-brand" />
          {t('home.haveCode')}
        </button>
      </div>

      {/* Leaderboard peek (top 10) */}
      {showLeaderboard && (
        <section className="space-y-2">
          <h2 className="flex items-center gap-1.5 text-sm font-medium text-fg">
            <Trophy aria-hidden className="h-4 w-4 text-brand" />
            {t('home.leaderboard')}
          </h2>
          {lbError || leaderboard === null ? (
            <p className="text-sm text-danger">{t('home.lbError')}</p>
          ) : lbLoading ? (
            <p className="text-sm text-fg-muted">…</p>
          ) : players.length === 0 ? (
            <p className="text-sm text-fg-muted">{t('home.noMatches')}</p>
          ) : (
            <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-bg-secondary">
              {players.map((p) => (
                <li key={p.userId} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="w-6 text-sm tabular-nums text-fg-muted">{p.rank}</span>
                  <span className="flex-1 truncate text-sm text-fg">{p.displayName}</span>
                  <span className="text-sm font-semibold tabular-nums text-brand">{p.elo}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Rules tutorial modal */}
      {tutorialOpen && <CpTutorialModal onClose={() => setTutorialOpen(false)} />}

      {/* Ranked queue sheet — shown while open */}
      {queueOpen && (
        <CpQueueSheet
          queue={queue}
          onClose={closeQueue}
          onEnter={() => {
            if (queue.matched) startMatch({ game: 'qcp', matchId: queue.matched });
          }}
        />
      )}

      {/* Creator invite sheet */}
      {inviteOpen && (
        <CpInviteSheet
          invite={invite}
          onClose={() => setInviteOpen(false)}
        />
      )}

      {/* Acceptor (I have a code) sheet */}
      {acceptOpen && (
        <CpAcceptInviteSheet
          onAccepted={onInviteAccepted}
          onClose={() => setAcceptOpen(false)}
        />
      )}
    </div>
  );
}
